import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://sohar.sa";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/home.jpg`;

interface SeoProps {
  title: string;
  description: string;
  path: string;
  lang?: "ar" | "en";
  image?: string;
  imageAlt?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const toAbsolute = (image?: string) => {
  if (!image) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`;
};

const Seo = ({
  title,
  description,
  path,
  lang = "ar",
  image,
  imageAlt,
  type = "website",
  noindex,
  jsonLd,
}: SeoProps) => {
  const url = `${SITE_URL}${path}`;
  const ogImage = toAbsolute(image);
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang={lang} href={url} />
      <link rel="alternate" hrefLang="x-default" href={SITE_URL} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content="Sohar Real Estate — صحار العقارية" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang === "ar" ? "ar_SA" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "ar" ? "en_US" : "ar_SA"} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt || title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@soharsa" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt || title} />

      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
